import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

// A design terv gomb-rendszere: alap = fehér, keretes, finom árnyék; primary = Stripe-szerű near-black;
// brand = MORGENS-zöld; magasságok 22/28/32/38px, rádiusz 8px, 13.5px/500 felirat, 14px ikonok.
const buttonVariants = cva(
  'text-md ease-atlas focus-visible:shadow-ring-focus inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-colors duration-150 focus-visible:outline-none active:translate-y-[0.5px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // GRADIENSES zöld — a neve SZÁNDÉKOSAN `cta`, nem `default`. SZŰK szerep (design.md):
        // CSAK a lista-oldalak „+ Új …" belépője (`ListNewButton`). Amíg `default` volt, ez volt a cva
        // alapértelmezése is, tehát MINDEN variáns nélkül írt (vagy `'default'`-ra kiértékelődő) gomb NÉMÁN
        // gradienses lett — mérve 16 elcsúszott hely két körben (dialógus-primary, RSVP-toggle, komment-
        // láthatóság-váltó, megerősítő-dialógus). Az alapértelmezés innentől a LAPOS `brand`: a gradiens
        // csak akkor jelenik meg, ha valaki EXPLICIT kiírja — véletlenül nem szivároghat vissza.
        cta: 'bg-gradient-to-r from-[var(--accent-grad-from)] to-[var(--accent-grad-to)] text-white shadow-sm hover:-translate-y-px hover:shadow-md',
        // LAPOS MORGENS-zöld — MINDEN elsődleges akció alapértelmezése: mentés, létrehozás, megerősítés.
        brand:
          'bg-brand text-brand-foreground hover:bg-brand-hover shadow-[0_1px_0_hsl(0_0%_0%/0.12),inset_0_1px_0_hsl(0_0%_100%/0.16)]',
        // Alap (a terv `.btn`-je): fehér/kártya-háttér, keret, finom árnyék.
        outline: 'border-border bg-card text-foreground shadow-xs hover:bg-accent border',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'text-muted-foreground hover:bg-accent hover:text-foreground',
        destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        warning:
          'shadow-xs bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400',
        link: 'text-brand-hover underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-[22px] rounded-md px-2 text-xs',
        sm: 'h-7 px-2.5 text-sm',
        default: 'h-8 px-3',
        lg: 'h-[38px] px-4 text-base',
        icon: 'size-[30px] rounded-md [&_svg]:size-4',
      },
    },
    // Az ALAPÉRTELMEZÉS a lapos `brand` (nem a gradiens `cta`) — lásd a `cta` fenti indoklását.
    defaultVariants: { variant: 'brand', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
